import { registerBuiltInApiProviders } from "@earendil-works/pi-ai";
import { ChatPanel, getAppStorage } from "@earendil-works/pi-web-ui";
import { LitElement, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { sparkModel, SPARK_PROVIDER } from "../spark-model.js";
import { asAgent, PiTaskAgent } from "../transport/pi-task-agent.js";
import "../components/mcp-connector.js";

registerBuiltInApiProviders();

interface TaskRecord {
	id: string;
	title: string;
	status: "starting" | "idle" | "running" | "done" | "failed" | "killed";
	createdAt: number;
	error?: string;
}

interface FileEntry {
	name: string;
	path: string;
	isDir: boolean;
	size: number;
	modified: number;
}

@customElement("cowork-tab")
export class CoworkTab extends LitElement {
	createRenderRoot() {
		return this;
	}

	/** "personal" (default) = the classic Cowork tab. "tenders" = the shared
	 *  Tenders workspace (issue #5): every API/WS call carries ?scope=tenders,
	 *  the backend maps it to the team-wide _tenders namespace, and some
	 *  tender-specific UI (Analyse starten, Schema editor) appears. */
	@property() scope: "personal" | "tenders" = "personal";

	@state() private tasks: TaskRecord[] = [];
	@state() private activeId?: string;
	@state() private files: FileEntry[] = [];
	@state() private newTitle = "";
	@state() private fileViewer?: FileViewerState;
	/** Current sub-directory within the workdir that the file pane is showing.
	 *  "" = workdir root. Click a dir → push its name; "Up" → drop the last
	 *  segment. The backend's /files endpoint takes this as the `sub` query. */
	@state() private currentSub = "";
	/** True while a drag is happening over the file pane — used to render a
	 *  highlighted drop zone so the user knows the drop target is live. */
	@state() private dragOver = false;
	/** True while an upload is in flight — disables the drop zone and shows
	 *  a tiny inline progress hint. */
	@state() private uploading = false;
	/** Human-readable "what is the agent doing right now" line, derived from
	 *  streamed agent events (issue #3). undefined = agent idle, bar hidden. */
	@state() private activity?: string;
	/** Draft for the mid-run steering input (issue #3): pi-web-ui's message
	 *  editor hard-blocks sending while the agent streams, but the backend
	 *  happily steers a running session — this input bridges that gap so
	 *  users can add info without waiting for the task to finish. */
	@state() private steerDraft = "";

	@query(".cowork-chat-slot") private chatSlot?: HTMLDivElement;

	private ws?: WebSocket;
	private agent?: PiTaskAgent;
	private chatPanel?: ChatPanel;

	/** Tenders: analysis-schema editor state (issue #5). */
	@state() private schemaOpen = false;
	@state() private schemaDraft = "";
	@state() private schemaBusy = false;

	/** Build a /api/tasks URL, carrying ?scope=tenders when this instance is
	 *  the Tenders tab so the backend routes to the shared namespace. */
	private api(path: string, params: Record<string, string> = {}): string {
		const qs = new URLSearchParams(params);
		if (this.scope === "tenders") qs.set("scope", "tenders");
		const q = qs.toString();
		return `/api/tasks${path}${q ? `?${q}` : ""}`;
	}

	async connectedCallback() {
		super.connectedCallback();
		this.classList.add("flex", "flex-1", "min-h-0");
		const existing = await getAppStorage().providerKeys.get(SPARK_PROVIDER);
		if (!existing) await getAppStorage().providerKeys.set(SPARK_PROVIDER, "proxied-by-backend");
		await this.refreshTasks();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.ws?.close();
	}

	private async refreshTasks() {
		const res = await fetch(this.api(""));
		if (!res.ok) return;
		const body = (await res.json()) as { tasks: TaskRecord[] };
		this.tasks = body.tasks;
	}

	private async createTask() {
		const title = this.newTitle.trim() || "Task";
		this.newTitle = "";
		const res = await fetch(this.api(""), {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ title }),
		});
		if (!res.ok) {
			const body = await res.json().catch(() => undefined);
			alert(`Could not create task: ${(body as { error?: string })?.error ?? res.statusText}`);
			return;
		}
		const { task } = (await res.json()) as { task: TaskRecord };
		this.tasks = [task, ...this.tasks];
		void this.openTask(task.id);
	}

	private async openTask(id: string) {
		if (this.activeId === id) return;
		this.ws?.close();
		this.activeId = id;
		this.chatPanel = undefined;
		this.fileViewer = undefined;
		this.currentSub = "";
		const proto = window.location.protocol === "https:" ? "wss" : "ws";
		const scopeQ = this.scope === "tenders" ? "?scope=tenders" : "";
		this.ws = new WebSocket(`${proto}://${window.location.host}/api/tasks/${id}/stream${scopeQ}`);
		this.agent = new PiTaskAgent(id, this.ws, sparkModel(), async (fileName, bytes) => {
			// Save prompt attachments as real files in the workdir so the
			// server-side agent can read them with its file tools; the prompt
			// only carries the path. Refreshing the pane makes the file show up
			// immediately alongside the user's message.
			const form = new FormData();
			form.append("file", bytes, fileName);
			const res = await fetch(this.api(`/${id}/upload`), { method: "POST", body: form });
			if (!res.ok) throw new Error(`upload_failed: ${res.status}`);
			const body = (await res.json()) as { entry: { path: string } };
			void this.refreshFiles();
			return body.entry.path;
		});
		this.ws.addEventListener("message", (e) => {
			let frame: { type: string; status?: TaskRecord["status"] };
			try {
				frame = JSON.parse(e.data as string);
			} catch {
				return;
			}
			if (frame.type === "status") {
				this.tasks = this.tasks.map((t) => (t.id === id && frame.status ? { ...t, status: frame.status } : t));
			}
			if (frame.type === "agent") {
				// Refresh file list when assistant turn ends, since pi may have written files.
				const evt = (frame as { event?: { type?: string } }).event;
				if (evt?.type === "agent_end" || evt?.type === "turn_end") void this.refreshFiles();
				this.updateActivity(evt);
			}
		});
		void this.refreshFiles();
		// Wait for the slot to render after activeId change, then mount imperatively.
		await this.updateComplete;
		await this.mountPanel();
	}

	private async mountPanel() {
		if (!this.chatSlot || !this.agent) return;
		const panel = new ChatPanel();
		// Mount first so agent-interface's connectedCallback fires in order.
		this.chatSlot.replaceChildren(panel);
		this.chatPanel = panel;
		await panel.setAgent(asAgent(this.agent), {
			// PiTaskAgent doesn't need an API key — the backend talks to vLLM
			// directly. Returning truthy from onApiKeyRequired skips the prompt
			// dialog if pi-web-ui ever decides the seeded providerKeys entry is
			// missing.
			onApiKeyRequired: async () => true,
			onBeforeSend: () => {
				console.log("[CoworkTab] onBeforeSend (sendMessage passed all gates)");
			},
		});
		// The Cowork/Tenders agent runs server-side on the Spark's Qwen — a
		// model picker here would only change local display state and confuse
		// people, so hide it (AgentInterface re-binds showModelSelector from
		// this property on every render).
		const ai = (panel as unknown as { agentInterface?: { enableModelSelector?: boolean } })
			.agentInterface;
		if (ai) ai.enableModelSelector = false;
	}

	private async refreshFiles() {
		if (!this.activeId) return;
		const res = await fetch(
			this.api(`/${this.activeId}/files`, this.currentSub ? { sub: this.currentSub } : {}),
		);
		if (!res.ok) {
			// If the dir no longer exists (e.g. pi deleted it) bail back to root
			// rather than leaving the pane wedged on a broken path.
			if (this.currentSub) {
				this.currentSub = "";
				void this.refreshFiles();
			}
			return;
		}
		const body = (await res.json()) as { entries: FileEntry[] };
		this.files = body.entries;
	}

	/** Descend into the directory named `name` (relative to the current sub). */
	private navigateInto(name: string) {
		this.currentSub = this.currentSub ? `${this.currentSub}/${name}` : name;
		this.fileViewer = undefined;
		void this.refreshFiles();
	}

	/** Pop one segment off the current sub, or no-op at root. */
	private navigateUp() {
		if (!this.currentSub) return;
		const segs = this.currentSub.split("/");
		segs.pop();
		this.currentSub = segs.join("/");
		this.fileViewer = undefined;
		void this.refreshFiles();
	}

	private navigateToCrumb(idx: number) {
		const segs = this.currentSub.split("/");
		this.currentSub = segs.slice(0, idx + 1).join("/");
		this.fileViewer = undefined;
		void this.refreshFiles();
	}

	private navigateRoot() {
		if (!this.currentSub) return;
		this.currentSub = "";
		this.fileViewer = undefined;
		void this.refreshFiles();
	}

	/** Upload one file into the current sub-directory. Used by both the
	 *  drag-drop handler and the file-picker button. */
	private async uploadOne(file: File) {
		if (!this.activeId) return;
		this.uploading = true;
		try {
			const fd = new FormData();
			fd.append("file", file, file.name);
			const res = await fetch(
				this.api(`/${this.activeId}/upload`, this.currentSub ? { path: this.currentSub } : {}),
				{
				method: "POST",
				body: fd,
			});
			if (!res.ok) {
				const body = await res.json().catch(() => ({}) as { error?: string });
				alert(`Upload failed: ${(body as { error?: string }).error ?? res.statusText}`);
			}
		} finally {
			this.uploading = false;
			await this.refreshFiles();
		}
	}

	private async uploadMany(files: FileList | File[]) {
		// Sequential, not parallel — the multipart plugin caps `files: 1` per
		// request and parallel POSTs would spam the backend. Plus the user
		// usually drops only one or two; this keeps the order predictable.
		for (const f of Array.from(files)) await this.uploadOne(f);
	}

	private onPaneDragOver(ev: DragEvent) {
		// Required to make `drop` fire on the pane. Setting dropEffect tells
		// the browser to render the copy cursor.
		ev.preventDefault();
		if (ev.dataTransfer) ev.dataTransfer.dropEffect = "copy";
		if (!this.dragOver) this.dragOver = true;
	}

	private onPaneDragLeave(ev: DragEvent) {
		// dragleave fires when the cursor crosses into child elements too;
		// only clear when actually leaving the pane.
		if (ev.currentTarget === ev.target) this.dragOver = false;
	}

	private onPaneDrop(ev: DragEvent) {
		ev.preventDefault();
		this.dragOver = false;
		const files = ev.dataTransfer?.files;
		if (files && files.length > 0) void this.uploadMany(files);
	}

	private async deleteFile(path: string, ev: Event) {
		ev.stopPropagation();
		if (!this.activeId) return;
		if (!confirm(`Delete "${path}"?`)) return;
		const res = await fetch(this.api(`/${this.activeId}/file`, { path }), {
			method: "DELETE",
		});
		if (!res.ok) {
			const body = await res.json().catch(() => ({}) as { error?: string });
			alert(`Delete failed: ${(body as { error?: string }).error ?? res.statusText}`);
			return;
		}
		// Drop the viewer if it was showing the just-deleted file.
		if (this.fileViewer?.path === path) this.fileViewer = undefined;
		await this.refreshFiles();
	}

	private async openFile(path: string) {
		if (!this.activeId) return;
		const mode = viewerModeFor(path);
		const rawUrl = this.api(`/${this.activeId}/raw`, { path });
		// For text files we still hit /file so we can show the contents inline
		// without an iframe round-trip; for PDFs/images we just point the
		// renderer at /raw and let the browser handle bytes natively.
		if (mode === "text") {
			const res = await fetch(this.api(`/${this.activeId}/file`, { path }));
			if (!res.ok) {
				this.fileViewer = { path, mode: "binary", rawUrl };
				return;
			}
			const body = (await res.json()) as { path: string; content: string };
			this.fileViewer = { path: body.path, mode: "text", textContent: body.content, rawUrl };
		} else {
			this.fileViewer = { path, mode, rawUrl };
		}
	}

	private async deleteTask(id: string, ev: Event) {
		ev.stopPropagation();
		if (!confirm("Delete this task and its files?")) return;
		await fetch(this.api(`/${id}`), { method: "DELETE" });
		this.tasks = this.tasks.filter((t) => t.id !== id);
		if (this.activeId === id) {
			this.activeId = undefined;
			this.ws?.close();
			this.agent = undefined;
			this.chatPanel = undefined;
		}
	}

	render() {
		return html`
			<div class="flex flex-1 min-h-0">
				${this.renderTaskList()}
				${this.activeId ? this.renderChat() : this.renderEmpty()}
				${this.activeId ? this.renderFiles() : ""}
			</div>
			${this.schemaOpen ? this.renderSchemaEditor() : ""}
		`;
	}

	private renderTaskList() {
		return html`
			<!-- Task-list scrolling (issue #4): actual fix is the min-height:0
			     rules in app.css; the min-h-0 class is not in the precompiled
			     Tailwind bundle. -->
			<div class="w-64 shrink-0 border-r border-border flex flex-col min-h-0">
				<div class="p-3 border-b border-border space-y-2">
					${this.scope === "tenders"
						? html`
								<!-- The Atlassian MCP connector is per-user; tender tasks run
								     under the shared namespace where no per-user credentials
								     exist, so the connector is hidden here. -->
								<div class="flex items-center justify-between">
									<span class="text-sm font-medium">Ausschreibungen</span>
									<button
										class="text-xs text-muted-foreground hover:text-foreground"
										title="Analyse-Schema bearbeiten (gilt für alle Tender-User)"
										@click=${() => this.openSchemaEditor()}
									>⚙ Schema</button>
								</div>
							`
						: html`<mcp-connector></mcp-connector>`}
					<input
						class="w-full px-2 py-1 rounded border border-input bg-background text-sm"
						placeholder=${this.scope === "tenders" ? "Neue Tender-Analyse…" : "New task title…"}
						.value=${this.newTitle}
						@input=${(e: Event) => (this.newTitle = (e.target as HTMLInputElement).value)}
						@keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.createTask()}
					/>
					<button
						class="w-full px-2 py-1 rounded bg-primary text-primary-foreground text-sm"
						@click=${() => this.createTask()}
					>
						${this.scope === "tenders" ? "Tender anlegen" : "Start task"}
					</button>
				</div>
				<div class="flex-1 overflow-y-auto">
					${this.tasks.length === 0
						? html`<div class="p-4 text-sm text-muted-foreground">No tasks yet.</div>`
						: this.tasks.map(
								(t) => html`
									<div
										class="p-3 border-b border-border cursor-pointer hover:bg-accent ${this.activeId === t.id ? "bg-accent" : ""}"
										@click=${() => this.openTask(t.id)}
									>
										<div class="flex justify-between items-start gap-2">
											<div class="font-medium text-sm truncate">${t.title}</div>
											<button
												class="text-xs text-muted-foreground hover:text-destructive"
												@click=${(e: Event) => this.deleteTask(t.id, e)}
											>
												✕
											</button>
										</div>
										<div class="text-xs text-muted-foreground mt-1">${statusLabel(t.status)}</div>
										${t.error ? html`<div class="text-xs text-destructive mt-1 truncate" title=${t.error}>${t.error}</div>` : ""}
									</div>
								`,
							)}
				</div>
			</div>
		`;
	}

	private renderEmpty() {
		if (this.scope === "tenders") {
			return html`
				<div class="flex-1 grid place-items-center text-muted-foreground p-8">
					<div class="max-w-md text-center space-y-2">
						<div class="text-lg font-medium">Tenders</div>
						<div class="text-sm">
							Lege links eine neue Tender-Analyse an, lade die Ausschreibungsunterlagen in der
							Dateiablage hoch und klicke dann auf „Analyse starten". Alle Tender-User sehen
							dieselben Analysen.
						</div>
					</div>
				</div>
			`;
		}
		return html`
			<div class="flex-1 grid place-items-center text-muted-foreground p-8">
				<div class="max-w-md text-center space-y-2">
					<div class="text-lg font-medium">Cowork</div>
					<div class="text-sm">
						Start a task on the left. Each task spawns a fresh pi-coding-agent session with its
						own filesystem under <code class="px-1 rounded bg-muted">data/tasks/&lt;id&gt;/</code>.
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Map streamed agent events to a one-line activity description shown above
	 * the chat (issue #3 — "why is cowork working?"). Tool starts carry the
	 * most information; agent_start/end bound the visible window.
	 */
	private updateActivity(evt?: { type?: string; toolName?: string; args?: unknown }) {
		if (!evt?.type) return;
		switch (evt.type) {
			case "agent_start":
				this.activity = "Denkt nach…";
				break;
			case "tool_execution_start":
				this.activity = describeToolCall(evt.toolName ?? "tool", evt.args);
				break;
			case "tool_execution_end":
				this.activity = "Arbeitet weiter…";
				break;
			case "agent_end":
				this.activity = undefined;
				break;
			default:
				break;
		}
	}

	/* ── Tenders (issue #5) ────────────────────────────────────────────── */

	/** Fetch the configured analysis prompt and hand it to the task's agent —
	 *  the whole "Analyse starten" flow: upload documents first, then click. */
	private async startAnalysis() {
		if (!this.agent) return;
		const res = await fetch("/api/tenders/prompt");
		if (!res.ok) {
			alert("Analyse-Schema konnte nicht geladen werden.");
			return;
		}
		const body = (await res.json()) as { prompt: string };
		void this.agent.prompt(body.prompt);
	}

	private async openSchemaEditor() {
		this.schemaBusy = true;
		this.schemaOpen = true;
		try {
			const res = await fetch("/api/tenders/prompt");
			if (!res.ok) throw new Error(res.statusText);
			const body = (await res.json()) as { prompt: string };
			this.schemaDraft = body.prompt;
		} catch {
			alert("Analyse-Schema konnte nicht geladen werden.");
			this.schemaOpen = false;
		} finally {
			this.schemaBusy = false;
		}
	}

	private async saveSchema() {
		this.schemaBusy = true;
		try {
			const res = await fetch("/api/tenders/prompt", {
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ prompt: this.schemaDraft }),
			});
			if (!res.ok) {
				const body = (await res.json().catch(() => ({}))) as { error?: string };
				alert(`Speichern fehlgeschlagen: ${body.error ?? res.statusText}`);
				return;
			}
			this.schemaOpen = false;
		} finally {
			this.schemaBusy = false;
		}
	}

	private renderSchemaEditor() {
		return html`
			<div class="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6">
				<div class="bg-background border border-border rounded-lg shadow-xl w-full max-w-3xl flex flex-col" style="max-height: 85vh">
					<div class="px-4 py-3 border-b border-border flex items-center justify-between">
						<div>
							<div class="font-medium text-sm">Analyse-Schema</div>
							<div class="text-xs text-muted-foreground">
								Dieser Prompt wird bei „Analyse starten" an den Agenten geschickt. Gilt für alle Tender-User.
							</div>
						</div>
						<button
							class="px-2 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
							@click=${() => (this.schemaOpen = false)}
						>✕</button>
					</div>
					<textarea
						class="flex-1 m-4 p-3 rounded border border-input bg-background text-sm font-mono resize-none"
						style="min-height: 50vh"
						.value=${this.schemaDraft}
						?disabled=${this.schemaBusy}
						@input=${(e: Event) => (this.schemaDraft = (e.target as HTMLTextAreaElement).value)}
					></textarea>
					<div class="px-4 pb-4 flex justify-end gap-2">
						<button
							class="px-3 py-1.5 rounded border border-border text-sm hover:bg-accent"
							@click=${() => (this.schemaOpen = false)}
						>Abbrechen</button>
						<button
							class="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm disabled:opacity-50"
							?disabled=${this.schemaBusy || !this.schemaDraft.trim()}
							@click=${() => this.saveSchema()}
						>Speichern</button>
					</div>
				</div>
			</div>
		`;
	}

	/* ── Steering (issue #3) ───────────────────────────────────────────── */

	/** Send additional info to the agent while it is running. The backend's
	 *  promptTask() sees the session is streaming and injects the message
	 *  with streamingBehavior:"steer", so the agent picks it up before its
	 *  next model call instead of after the whole task finishes. */
	private sendSteer() {
		const text = this.steerDraft.trim();
		if (!text || !this.agent) return;
		this.steerDraft = "";
		void this.agent.prompt(text);
	}

	private renderChat() {
		// Stable mount slot. ChatPanel is appended in mountPanel() via
		// replaceChildren — putting ${this.chatPanel} directly in the template
		// causes Lit to detach/reattach the agent-interface on re-renders, which
		// breaks streaming. flex-col stretches the panel to full slot width.
		return html`
			<div class="flex-1 min-w-0 flex flex-col">
				${this.scope === "tenders" && !this.activity
					? html`<div
							class="px-3 py-1.5 border-b border-border text-xs flex items-center gap-2 shrink-0"
						>
							<button
								class="px-2 py-1 rounded bg-primary text-primary-foreground text-xs"
								title="Schickt das konfigurierte Analyse-Schema an den Agenten"
								@click=${() => this.startAnalysis()}
							>
								▶ Analyse starten
							</button>
							<span class="text-muted-foreground truncate">
								Erst Unterlagen rechts hochladen, dann Analyse starten.
							</span>
						</div>`
					: ""}
				${this.activity
					? html`<div
							class="px-3 py-1.5 border-b border-border text-xs flex items-center gap-2 shrink-0"
						>
							<span class="inline-block w-2 h-2 rounded-full bg-accent animate-pulse shrink-0"></span>
							<span class="truncate text-muted-foreground shrink-0 max-w-[40%]" title=${this.activity}
								>${this.activity}</span
							>
							<!-- Mid-run steering (issue #3): the main editor below is
							     locked while streaming, so this is the way to hand the
							     running agent extra info without waiting. -->
							<input
								class="flex-1 min-w-0 px-2 py-1 rounded border border-input bg-background text-xs"
								placeholder="Dem laufenden Task etwas mitgeben… (Enter zum Senden)"
								.value=${this.steerDraft}
								@input=${(e: Event) => (this.steerDraft = (e.target as HTMLInputElement).value)}
								@keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.sendSteer()}
							/>
							<button
								class="px-2 py-1 rounded bg-primary text-primary-foreground text-xs disabled:opacity-50"
								?disabled=${!this.steerDraft.trim()}
								@click=${() => this.sendSteer()}
							>
								Senden
							</button>
						</div>`
					: ""}
				<div class="cowork-chat-slot flex-1 min-h-0 min-w-0 flex flex-col"></div>
			</div>
		`;
	}

	private renderFiles() {
		const crumbs = this.currentSub ? this.currentSub.split("/") : [];
		return html`
			<div
				class="w-72 shrink-0 border-l border-border flex flex-col min-h-0 relative ${this.dragOver ? "ring-2 ring-primary ring-inset" : ""}"
				@dragover=${(e: DragEvent) => this.onPaneDragOver(e)}
				@dragleave=${(e: DragEvent) => this.onPaneDragLeave(e)}
				@drop=${(e: DragEvent) => this.onPaneDrop(e)}
			>
				<div class="p-3 border-b border-border flex justify-between items-center gap-2">
					<div class="text-sm font-medium">Files</div>
					<div class="flex items-center gap-2">
						<label class="text-xs text-muted-foreground hover:text-foreground cursor-pointer" title="Upload">
							⬆
							<input
								type="file"
								class="hidden"
								@change=${(e: Event) => {
									const input = e.target as HTMLInputElement;
									if (input.files && input.files.length > 0) {
										void this.uploadMany(input.files);
									}
									input.value = "";
								}}
							/>
						</label>
						<button
							class="text-xs text-muted-foreground hover:text-foreground"
							title="Refresh"
							@click=${() => this.refreshFiles()}
						>↻</button>
					</div>
				</div>

				<!-- Breadcrumb path. Always renders the workdir-root crumb so users
				     in a deep subdir have a one-click bail-out. Each segment is
				     clickable; the last one is the current dir (no link). -->
				<div class="px-3 py-1.5 border-b border-border text-xs text-muted-foreground flex flex-wrap items-center gap-1">
					<button
						class="hover:text-foreground hover:underline ${crumbs.length === 0 ? "text-foreground font-medium" : ""}"
						@click=${() => this.navigateRoot()}
					>
						/
					</button>
					${crumbs.map((seg, i) =>
						i === crumbs.length - 1
							? html`<span>/</span><span class="text-foreground font-medium">${seg}</span>`
							: html`
									<span>/</span>
									<button class="hover:text-foreground hover:underline" @click=${() => this.navigateToCrumb(i)}>
										${seg}
									</button>
								`,
					)}
				</div>

				<div class="flex-1 overflow-y-auto text-sm">
					${crumbs.length > 0
						? html`
								<div
									class="px-3 py-1 cursor-pointer hover:bg-accent text-muted-foreground"
									@click=${() => this.navigateUp()}
								>
									⤴ ..
								</div>
							`
						: ""}
					${this.files.length === 0
						? html`<div class="p-3 text-muted-foreground">
								${this.uploading ? "Uploading…" : "Empty — drop files here to upload."}
							</div>`
						: this.files.map((f) => this.renderFileRow(f))}
				</div>

				<!-- Drop-zone overlay. Pointer-events:none so the native dragover/
				     drop on the parent still see the drag; this is purely visual. -->
				${this.dragOver
					? html`<div
							class="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary grid place-items-center text-sm font-medium text-primary pointer-events-none"
						>
							Drop to upload to ${this.currentSub || "/"}
						</div>`
					: ""}
				${this.uploading
					? html`<div class="px-3 py-1 text-xs text-muted-foreground border-t border-border">Uploading…</div>`
					: ""}
				${this.fileViewer ? this.renderFileViewer() : ""}
			</div>
		`;
	}

	private renderFileRow(f: FileEntry) {
		// We pass `f.name` (not `f.path`) to navigateInto so the new currentSub
		// composes from the displayed crumb stack — keeps the path canonical
		// regardless of how listFiles formatted the relative path field.
		const onClick = f.isDir ? () => this.navigateInto(f.name) : () => this.openFile(f.path);
		return html`
			<div
				class="px-3 py-1 cursor-pointer hover:bg-accent flex justify-between items-center group"
				@click=${onClick}
			>
				<span class="truncate">${f.isDir ? "📁" : "📄"} ${f.name}</span>
				<span class="flex items-center gap-2">
					${f.isDir
						? ""
						: html`<span class="text-xs text-muted-foreground">${formatSize(f.size)}</span>`}
					${f.isDir
						? ""
						: html`
								<button
									class="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
									title="Delete"
									@click=${(e: Event) => this.deleteFile(f.path, e)}
								>
									✕
								</button>
							`}
				</span>
			</div>
		`;
	}

	private renderFileViewer() {
		const v = this.fileViewer;
		if (!v) return "";
		// Header: filename, "Open" link (escape hatch — opens the raw URL in a
		// new tab so the user can use the browser's native viewer if our inline
		// rendering ever falls short), and a clearly-clickable Close button.
		return html`
			<div class="border-t border-border flex flex-col" style="max-height: 50vh; min-height: 200px">
				<div class="px-3 py-2 text-xs font-medium border-b border-border flex justify-between items-center gap-2">
					<span class="truncate flex-1" title=${v.path}>${v.path}</span>
					<a
						class="px-2 py-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
						href=${v.rawUrl}
						target="_blank"
						rel="noopener"
						title="Open in new tab"
					>↗</a>
					<button
						class="px-2 py-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
						@click=${() => (this.fileViewer = undefined)}
						title="Close preview"
					>Close ✕</button>
				</div>
				${this.renderViewerBody(v)}
			</div>
		`;
	}

	private renderViewerBody(v: FileViewerState) {
		switch (v.mode) {
			case "text":
				return html`<pre class="p-3 text-xs whitespace-pre-wrap break-all overflow-y-auto flex-1">${v.textContent ?? ""}</pre>`;
			case "pdf":
				// Browser's built-in PDF viewer handles paging/scroll/zoom;
				// nothing for us to do beyond pointing the iframe at /raw.
				return html`<iframe class="flex-1 w-full border-0" src=${v.rawUrl} title=${v.path}></iframe>`;
			case "image":
				return html`
					<div class="p-3 overflow-auto flex-1 grid place-items-center bg-muted/20">
						<img src=${v.rawUrl} alt=${v.path} class="max-w-full max-h-full object-contain" />
					</div>
				`;
			case "binary":
				return html`
					<div class="p-4 text-sm text-muted-foreground space-y-2">
						<div>Binary file — no inline preview.</div>
						<a class="underline hover:text-foreground" href=${v.rawUrl} download>Download</a>
					</div>
				`;
		}
	}
}

type ViewerMode = "text" | "pdf" | "image" | "binary";

interface FileViewerState {
	path: string;
	mode: ViewerMode;
	/** Always set; used by the "Open in new tab" link, by <iframe>/<img>, and
	 *  by the Download fallback for unknown binary types. */
	rawUrl: string;
	/** Only populated for `mode === "text"` — the inline content rendered in <pre>. */
	textContent?: string;
}

/**
 * Pick how to render a file based on extension. We bias toward "text" for
 * common source/code extensions because pi tends to produce a lot of those,
 * and reading them inline is way more useful than a Download link.
 */
function viewerModeFor(path: string): ViewerMode {
	const ext = path.toLowerCase().split(".").pop() ?? "";
	if (ext === "pdf") return "pdf";
	if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "image";
	if (
		[
			"txt", "md", "log", "json", "html", "htm", "xml", "csv", "yaml", "yml", "eml",
			"ts", "tsx", "js", "jsx", "mjs", "cjs",
			"py", "rb", "go", "rs", "java", "kt", "swift",
			"c", "h", "cc", "cpp", "hpp",
			"css", "scss", "less",
			"sh", "bash", "zsh", "fish",
			"toml", "ini", "env", "conf", "cfg",
			"sql", "graphql", "gql",
		].includes(ext)
	) {
		return "text";
	}
	// Files with no extension (Dockerfile, Makefile, README) — try as text.
	if (!ext || ext === path.toLowerCase()) return "text";
	return "binary";
}

/**
 * One-line description of a tool call for the activity bar. Pulls the most
 * telling argument per tool (command for bash, path for file tools, …) and
 * truncates so the bar never wraps.
 */
function describeToolCall(toolName: string, args: unknown): string {
	const a = (args ?? {}) as Record<string, unknown>;
	const detail =
		firstString(a.command) ??
		firstString(a.path, a.file_path, a.filePath) ??
		firstString(a.pattern, a.query) ??
		"";
	const labels: Record<string, string> = {
		bash: "Führt Befehl aus",
		read: "Liest Datei",
		write: "Schreibt Datei",
		edit: "Bearbeitet Datei",
		grep: "Durchsucht Dateien",
		find: "Sucht Dateien",
		ls: "Listet Verzeichnis",
	};
	const label = labels[toolName] ?? `Nutzt ${toolName}`;
	return detail ? `${label}: ${truncate(detail, 100)}` : `${label}…`;
}

function firstString(...vals: unknown[]): string | undefined {
	for (const v of vals) {
		if (typeof v === "string" && v.trim()) return v.trim();
	}
	return undefined;
}

function truncate(s: string, n: number): string {
	return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

function statusLabel(s: TaskRecord["status"]): string {
	switch (s) {
		case "starting":
			return "starting…";
		case "running":
			return "running";
		case "idle":
			return "idle";
		case "done":
			return "done";
		case "failed":
			return "failed";
		case "killed":
			return "killed";
	}
}

function formatSize(n: number): string {
	if (n < 1024) return `${n}B`;
	if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
	return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
